import type { DocumentRecord } from "@shyne/shared/api";
import { LinkableProperty, ServicedProperty, VendorDetail } from "@/repositories/vendors.repository";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadAllResponse, uuid } from "@/interfaces";
import {
  deleteVendorDocument,
  getVendorDetail,
  listVendorDocuments,
  uploadVendorDocument,
  listVendorProperties,
  addVendorProperty,
  removeVendorProperty,
  getAvailableProperties,
} from "@/app/pmc/vendors/vendors.detail/vendor-detail.api.ts";

interface VendorsDetailState {
  detail: VendorDetail | null;
  loading: boolean;
  error: string | null;
  deletingDoc: boolean;
  documents: DocumentRecord[];
  docLoading: boolean;
  docPage: number;
  docPageSize: number;
  docTotal: number;
  properties: ServicedProperty[];
  propLoading: boolean;
  propPage: number;
  propPageSize: number;
  propTotal: number;
  availableProperties: LinkableProperty[];
  addingProperty: boolean;
  removingProperty: boolean;
}

const initialState: VendorsDetailState = {
  detail: null,
  loading: false,
  error: null,
  deletingDoc: false,
  documents: [],
  docLoading: false,
  docPage: 1,
  docPageSize: 10,
  docTotal: 0,
  properties: [],
  propLoading: false,
  propPage: 1,
  propPageSize: 10,
  propTotal: 0,
  availableProperties: [],
  addingProperty: false,
  removingProperty: false,
};

export const fetchVendorDetail = createAsyncThunk<VendorDetail, uuid>(
  "vendorDetail/fetch",
  async (vendorId) => await getVendorDetail(vendorId)
);

export const uploadDoc = createAsyncThunk<DocumentRecord, { vendorId: uuid; file: File; expiresAt?: string | Date }>(
  "vendorDetail/uploadDoc",
  async ({ vendorId, file, expiresAt }) => await uploadVendorDocument(vendorId, file, expiresAt)
);

export const deleteDoc = createAsyncThunk<uuid, uuid>("vendorDetail/deleteDoc", async (docId) => {
  await deleteVendorDocument(docId);
  return docId;
});

export const fetchVendorDocuments = createAsyncThunk<
  ReadAllResponse<DocumentRecord>,
  {
    vendorId: uuid;
    page: number;
    pageSize: number;
  }
>(
  "vendorDetail/fetchDocs",
  async ({ vendorId, page, pageSize }) => await listVendorDocuments(vendorId, page, pageSize)
);

export const fetchVendorProperties = createAsyncThunk<
  ReadAllResponse<ServicedProperty>,
  {
    vendorId: uuid;
    page: number;
    pageSize: number;
  }
>(
  "vendorDetail/fetchProperties",
  async ({ vendorId, page, pageSize }) => await listVendorProperties(vendorId, page, pageSize)
);

export const addProperty = createAsyncThunk<ServicedProperty, { vendorId: uuid; propertyId: uuid }>(
  "vendorDetail/addProperty",
  async ({ vendorId, propertyId }) => await addVendorProperty(vendorId, propertyId)
);

export const removeProperty = createAsyncThunk<uuid, { vendorId: uuid; propertyId: uuid }>(
  "vendorDetail/removeProperty",
  async ({ vendorId, propertyId }) => {
    await removeVendorProperty(vendorId, propertyId);
    return propertyId;
  }
);

export const fetchAvailableProperties = createAsyncThunk<LinkableProperty[], { vendorId: uuid; search?: string }>(
  "vendorDetail/fetchAvailableProperties",
  async ({ vendorId, search }) => await getAvailableProperties(vendorId, search)
);

const vendorDetailSlice = createSlice({
  name: "vendorDetail",
  initialState,
  reducers: {
    clearDetail(state) {
      state.detail = null;
      state.loading = false;
      state.error = null;
    },
    setDocPage(state, action: PayloadAction<number>) {
      state.docPage = action.payload;
    },
    setDocPageSize(state, action: PayloadAction<number>) {
      state.docPageSize = action.payload;
    },
    setPropPage(state, action: PayloadAction<number>) {
      state.propPage = action.payload;
    },
    setPropPageSize(state, action: PayloadAction<number>) {
      state.propPageSize = action.payload;
    },
  },
  extraReducers: (b) =>
    b
      .addCase(fetchVendorDetail.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchVendorDetail.fulfilled, (s, a) => {
        s.loading = false;
        s.detail = a.payload;
      })
      .addCase(fetchVendorDetail.rejected, (s, a) => {
        s.loading = false;
        s.detail = null;
        s.error = a.error.message ?? "Failed to load vendor";
      })
      .addCase(deleteDoc.pending, (s) => {
        s.deletingDoc = true;
      })
      .addCase(deleteDoc.fulfilled, (s) => {
        s.deletingDoc = false;
      })
      .addCase(deleteDoc.rejected, (s) => {
        s.deletingDoc = false;
      })
      .addCase(fetchVendorDocuments.pending, (s) => {
        s.docLoading = true;
      })
      .addCase(fetchVendorDocuments.fulfilled, (s, a: PayloadAction<ReadAllResponse<DocumentRecord>>) => {
        s.docLoading = false;
        s.documents = a.payload.data;
        s.docTotal = a.payload.count ?? 0;
      })
      .addCase(fetchVendorDocuments.rejected, (s) => {
        s.docLoading = false;
      })
      .addCase(fetchVendorProperties.pending, (s) => {
        s.propLoading = true;
      })
      .addCase(fetchVendorProperties.fulfilled, (s, a: PayloadAction<ReadAllResponse<ServicedProperty>>) => {
        s.propLoading = false;
        s.properties = a.payload.data;
        s.propTotal = a.payload.count ?? 0;
      })
      .addCase(fetchVendorProperties.rejected, (s) => {
        s.propLoading = false;
      })
      .addCase(addProperty.pending, (s) => {
        s.addingProperty = true;
      })
      .addCase(addProperty.fulfilled, (s) => {
        s.addingProperty = false;
      })
      .addCase(addProperty.rejected, (s) => {
        s.addingProperty = false;
      })
      .addCase(removeProperty.pending, (s) => {
        s.removingProperty = true;
      })
      .addCase(removeProperty.fulfilled, (s) => {
        s.removingProperty = false;
      })
      .addCase(removeProperty.rejected, (s) => {
        s.removingProperty = false;
      })
      .addCase(fetchAvailableProperties.fulfilled, (s, a: PayloadAction<LinkableProperty[]>) => {
        s.availableProperties = a.payload;
      }),
});

export const { clearDetail, setDocPage, setDocPageSize, setPropPage, setPropPageSize } = vendorDetailSlice.actions;
const vendorDetailReducer = vendorDetailSlice.reducer;
export default vendorDetailReducer;
